import apiClient, { ApiParams } from '@/utility/apiClient'
import { handleApiError } from '@/lib/api/utils/errorHandler'
import {
  CompanyResponse,
  CompanyListResponse,
  RoleModelFinderCompanyResponse,
  RoleModelFinderBannerVisibilityResponse,
  CompanySearchParams,
  CompanyFilterParams,
  CompanyContentParams,
  CompanyTalentsParams,
  CompanyRecruitmentsParams,
} from '@/lib/api/types/api'

/**
 * Companies API service
 * Based on the Rails routes.rb file
 * Implements the /api/companies endpoints
 */
export const CompaniesService = {
  /**
   * Get all companies
   * Implements the GET /api/companies endpoint
   * @param params Optional query parameters
   */
  getCompanies: async (params: CompanyFilterParams = {}): Promise<CompanyListResponse> => {
    try {
      const url = 'companies'
      const response = await apiClient.request('GET', url, {
        params: params as ApiParams,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company by ID
   * Implements the GET /api/companies/:id endpoint
   * @param companyId The ID of the company to retrieve
   */
  getCompanyById: async (companyId: string): Promise<CompanyResponse> => {
    try {
      const url = `companies/${companyId}`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Search companies
   * Implements the GET /api/search/companies endpoint
   * @param params Company search parameters
   */
  searchCompanies: async (params: CompanySearchParams): Promise<CompanyListResponse> => {
    try {
      const url = 'search/companies'
      const response = await apiClient.request('GET', url, {
        params: params as ApiParams,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get preview of company by ID
   */
  getPreviewCompany: async (companyId: string): Promise<any> => {
    try {
      const url = `preview/companies/${companyId}`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company common info
   */
  getCompanyInfo: async (
    companyId: string,
    isPreview: boolean = false,
    options?: any,
  ): Promise<any> => {
    try {
      const baseUrl = isPreview
        ? `preview/companies/${companyId}/common-info`
        : `companies/${companyId}/common-info`
      const response = await apiClient.request('GET', baseUrl, options)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company book articles
   */
  getCompanyBookArticles: async (companyId: string, bookId: string): Promise<any> => {
    try {
      const url = `companies/${companyId}/books/${bookId}/articles`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company albums
   */
  getCompanyAlbums: async (companyId: string): Promise<any> => {
    try {
      const url = `companies/${companyId}/albums`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company books
   */
  getCompanyBooks: async (companyId: string, params: CompanyContentParams = {}): Promise<any> => {
    try {
      const url = `companies/${companyId}/books`
      const response = await apiClient.request('GET', url, { params: params as ApiParams })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company book detail
   */
  getCompanyBookDetail: async (
    companyId: string,
    bookId: string,
    options: { page?: string; skipRequestCancellation?: boolean } = {},
  ): Promise<any> => {
    try {
      const url = `companies/${companyId}/books/${bookId}`
      const { skipRequestCancellation, ...params } = options
      const response = await apiClient.request('GET', url, {
        params,
        skipRequestCancellation,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company detailed info
   */
  getCompanyDetailedInfo: async (companyId: string, isPreview: boolean = false): Promise<any> => {
    try {
      const baseUrl = isPreview
        ? `preview/companies/${companyId}/company-info`
        : `companies/${companyId}/company-info`

      const response = await apiClient.request('GET', baseUrl)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company contents
   */
  getCompanyContents: async (
    companyId: string,
    params: CompanyContentParams = {},
  ): Promise<any> => {
    try {
      const url = `companies/${companyId}/contents`
      const response = await apiClient.request('GET', url, {
        params: params as ApiParams,
        timeout: 10000, // 10 second timeout
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company media releases
   */
  getCompanyMediaReleases: async (
    companyId: string,
    params: CompanyContentParams = {},
  ): Promise<any> => {
    try {
      const url = `companies/${companyId}/media-releases`
      const response = await apiClient.request('GET', url, {
        params: params as ApiParams,
        skipRequestCancellation: true,
      })
      return response.data
    } catch (error) {
      // 404エラーの場合は空のレスポンスを返す（Vue版と同じ動作）
      if ((error as any).status === 404) {
        return {
          records: [],
          pageMeta: {
            currentPage: 1,
            totalPages: 1,
            totalContent: 0,
          },
          filters: [],
        }
      }

      handleApiError(error)
    }
  },

  /**
   * Get all companies list
   */
  getAllCompanies: async (params: CompanyContentParams = {}): Promise<any> => {
    try {
      const url = 'companies_all'
      const response = await apiClient.request('GET', url, { params: params as ApiParams })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get company talents
   * Implements the GET /api/companies/:id/talents endpoint
   * @param companyId The ID of the company
   * @param params Optional query parameters (years_of_service, occupation, background_of_joining, page)
   */
  getCompanyTalents: async (companyId: string, params: CompanyTalentsParams = {}): Promise<any> => {
    try {
      const url = `companies/${companyId}/talents`
      // 重複リクエストをスキップしないように設定
      const response = await apiClient.request('GET', url, {
        params: params as ApiParams,
        skipRequestCancellation: true,
      })
      return response.data
    } catch (error) {
      // 404エラーの場合は空のレスポンスを返す（Vue版と同じ動作）
      if ((error as any).status === 404) {
        return {
          records: [],
          page_meta: {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            pageLimit: 12,
          },
          filters: {
            backgroundOfJoining: [],
            occupation: [],
            yearsOfService: [],
          },
        }
      }

      handleApiError(error)
    }
  },

  /**
   * Get company recruitments
   * Implements the GET /api/companies/:id/recruitment_informations endpoint
   * @param companyId The ID of the company
   * @param params Optional query parameters (filter, page)
   */
  getCompanyRecruitments: async (
    companyId: string,
    params: CompanyRecruitmentsParams = {},
  ): Promise<any> => {
    try {
      const url = `companies/${companyId}/recruitment_informations`
      const response = await apiClient.request('GET', url, {
        params: params as ApiParams,
        skipRequestCancellation: true,
      })
      return response.data
    } catch (error) {
      // 404エラーの場合は空のレスポンスを返す
      if ((error as any).status === 404) {
        return {
          records: [],
          pageMeta: {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
          },
          filters: [],
        }
      }

      handleApiError(error)
    }
  },

  /**
   * Get companies for role model finder
   * Implements the GET /api/role_model_finder/companies endpoint
   */
  getRoleModelFinderCompanies: async (): Promise<RoleModelFinderCompanyResponse[]> => {
    try {
      const url = 'role_model_finder/companies'
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get role model finder banner visibility
   * Implements the GET /api/role_model_finder/companies/{openUrl}/banner_visibility endpoint
   * @param openUrl The open URL of the company
   * @returns boolean
   */
  getRoleModelFinderBannerVisibility: async (
    openUrl: string,
  ): Promise<RoleModelFinderBannerVisibilityResponse> => {
    try {
      const url = `role_model_finder/companies/${openUrl}/banner_visibility`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },
}
